import React from 'react';
import { formatDateTimeIst, interactionPanelState } from '../../utils/timeOnSystem';

const plural = (n, one, many = `${one}s`) => (n === 1 ? one : many);

function Tile({ label, value, sub }) {
  return (
    <div className="flex-1 min-w-[150px] rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">{value}</div>
      {sub ? <div className="mt-0.5 text-xs text-gray-500">{sub}</div> : null}
    </div>
  );
}

function Note({ children }) {
  return (
    <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-600">
      {children}
    </p>
  );
}

/**
 * One message per reason the panel can be empty, so "not connected yet" never
 * reads the same as "nobody clicked anything".
 */
function emptyMessage(state, data) {
  if (state === 'unavailable') {
    return 'Page views and clicks start once the latest backend update is deployed.';
  }
  if (state === 'not_connected') {
    return 'Mixpanel is not connected yet. Page views and clicks appear here once a Mixpanel service account is set up and the sync is switched on.';
  }
  if (state === 'waiting') {
    return data?.mixpanel?.last_error
      ? `The last Mixpanel sync failed: ${data.mixpanel.last_error}`
      : 'Mixpanel is connected. Page views and clicks appear after the first sync, which runs every two hours.';
  }
  return null;
}

function TopTable({ title, rows, valueLabel, getKey, getLabel, getValue }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="w-full min-w-[380px] border-collapse text-sm">
        <thead>
          <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
            <th className="px-4 py-2.5 text-left font-medium">{title}</th>
            <th className="px-4 py-2.5 text-right font-medium">{valueLabel}</th>
            <th className="px-4 py-2.5 text-right font-medium">People</th>
          </tr>
        </thead>
        <tbody>
          {!rows.length ? (
            <tr>
              <td colSpan={3} className="px-4 py-6 text-center text-gray-500">
                None in this period.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={getKey(row)} className="border-t border-gray-100">
                <td className="max-w-[260px] truncate px-4 py-2.5 text-gray-900" title={getLabel(row)}>
                  {getLabel(row)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-gray-900">
                  {getValue(row)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{row.people}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Page views and clicks, from Mixpanel.
 *
 * Background events — API timing, token refreshes, sign-in steps — are left out
 * of every figure, and the panel says how many, so the reader can see what was
 * removed rather than trust that it was.
 */
const InteractionsPanel = ({ data, isLoading, isError }) => {
  if (isLoading) {
    return (
      <section className="mt-6 rounded-lg border border-gray-200 bg-white py-8 text-center text-sm text-gray-500">
        Loading page views and clicks…
      </section>
    );
  }
  if (isError) {
    return (
      <section className="mt-6 rounded-lg border border-gray-200 bg-white py-8 text-center text-sm text-red-600">
        Could not load page views and clicks. Try again in a moment.
      </section>
    );
  }
  const state = interactionPanelState(data);
  if (state === 'hidden') return null;
  const message = emptyMessage(state, data);
  const totals = data.totals || {};

  return (
    <section className="mt-6">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900">Page views and clicks</h2>
        {data.mixpanel?.last_synced_at ? (
          <span className="text-xs text-gray-500">
            from Mixpanel · last synced {formatDateTimeIst(data.mixpanel.last_synced_at)}
          </span>
        ) : null}
      </div>

      {message ? (
        <Note>{message}</Note>
      ) : (
        <>
          <div className="flex flex-wrap gap-3">
            <Tile label="Page views" value={totals.page_views ?? 0} sub="screens opened" />
            <Tile label="Clicks" value={totals.clicks ?? 0} sub="buttons and items clicked" />
            <Tile label="Other actions" value={totals.actions ?? 0} sub="filters, tabs, forms, searches" />
            <Tile
              label="People"
              value={totals.people ?? 0}
              sub={`${plural(totals.people ?? 0, 'person', 'people')} with any of these`}
            />
          </div>
          <div className="mt-3 grid gap-3 lg:grid-cols-2">
            <TopTable
              title="Most opened screens"
              valueLabel="Views"
              rows={data.top_pages || []}
              getKey={(r) => r.path}
              getLabel={(r) => r.path}
              getValue={(r) => r.views}
            />
            <TopTable
              title="Most clicked"
              valueLabel="Clicks"
              rows={data.top_clicks || []}
              getKey={(r) => `${r.event}|${r.detail}`}
              getLabel={(r) => r.label}
              getValue={(r) => r.clicks}
            />
          </div>
          <p className="mt-2 max-w-3xl text-xs leading-relaxed text-gray-500">
            {data.system_events} background {plural(data.system_events, 'event')} — API
            timing, token refreshes, sign-in steps — are left out of these figures.{' '}
            {data.days_synced} {plural(data.days_synced, 'day')} in this period{' '}
            {data.days_synced === 1 ? 'has' : 'have'} been synced. Activity before sign-in
            cannot be tied to a person, so it is not counted.
          </p>
        </>
      )}
    </section>
  );
};

/** One person's page views and clicks, for the drill-down. */
export function UserInteractionSummary({ data, isLoading, isError }) {
  const state = interactionPanelState(data);
  const message = state === 'ready' ? null : emptyMessage(state, data);
  const totals = data?.totals || {};
  return (
    <div className="mt-5 border-t border-gray-100 pt-4">
      <h3 className="text-sm font-semibold text-gray-900">Pages and clicks</h3>
      {isLoading ? (
        <div className="py-4 text-center text-sm text-gray-500">Loading…</div>
      ) : isError ? (
        <div className="py-4 text-center text-sm text-red-600">
          Could not load page views and clicks. Try again in a moment.
        </div>
      ) : message ? (
        <p className="mt-2 text-xs text-gray-500">{message}</p>
      ) : state === 'hidden' ? null : (
        <>
          <p className="mt-1 text-sm text-gray-700">
            {totals.page_views ?? 0} {plural(totals.page_views ?? 0, 'page view')} ·{' '}
            {totals.clicks ?? 0} {plural(totals.clicks ?? 0, 'click')} ·{' '}
            {totals.actions ?? 0} other {plural(totals.actions ?? 0, 'action')}
          </p>
          <div className="mt-2 grid gap-4 sm:grid-cols-2">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500">Screens</div>
              <ul className="mt-1 space-y-1 text-sm">
                {(data.top_pages || []).slice(0, 5).map((p) => (
                  <li key={p.path} className="flex justify-between gap-3">
                    <span className="truncate text-gray-900" title={p.path}>{p.path}</span>
                    <span className="tabular-nums text-gray-600">{p.views}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500">Clicks</div>
              <ul className="mt-1 space-y-1 text-sm">
                {(data.top_clicks || []).slice(0, 5).map((c) => (
                  <li key={`${c.event}|${c.detail}`} className="flex justify-between gap-3">
                    <span className="truncate text-gray-900" title={c.label}>{c.label}</span>
                    <span className="tabular-nums text-gray-600">{c.clicks}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default InteractionsPanel;
